import React, { useEffect } from 'react';
import { applyTranslation } from '@/utils/translation';
import ReactDOM from 'react-dom/client';
import '@/styles/global.scss';
import './style.css';
import '@/styles/__custom-antd.scss';
import '@/styles/globalOverride.scss'; // for oveririding styles of antd components
import '@/styles/gtranslate-override.css'; // for hiding Google Translate elements
import '@/styles/language-selector.css'; // for styling the language selector
import { Provider } from 'react-redux';
import { store } from '@/store/index';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ConfigProvider } from 'antd';

import { ResellerGlobalProvider } from './v1/context/resellerContext';

import { GlobalProvider } from './v1/context/GlobalContext';
import 'antd/dist/reset.css'; // For Ant Design 5.x
import { ToastContainer } from 'react-toastify'; // Import ToastContainer
import 'react-toastify/dist/ReactToastify.css'; // Import the default CSS for Toastify

import { PostHogProvider } from 'posthog-js/react';
import AppRouter from './routes';
import VersionChecker from './versionChecker';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import AudioPlayerToolbar from './components/AudioPlayerToolbar/AudioPlayerToolbar';
import ErrorBoundary from './ErrorBoundary';

// Add type declaration for gtranslateSettings

const options = {
	// api_host: import.meta.env.VITE_CLOUD_FRONT_DOMAIN_URL,
	api_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
	// ui_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
	session_recording: {
		maskAllInputs: false,
		maskInputOptions: {
			password: false,
			color: false,
			date: false,
			'datetime-local': false,
			email: false,
			month: false,
			number: false,
			range: false,
			search: false,
			tel: false,
			text: false,
			time: false,
			url: false,
			week: false,
			textarea: false,
			select: false,
		},
	},
};

const queryClient = new QueryClient();

// DONOT define VITE_APP_SENTRY_DSN in development .env file
if (import.meta.env.VITE_APP_SENTRY_DSN) {
	Sentry.init({
		dsn: import.meta.env.VITE_APP_SENTRY_DSN,
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: ['localhost', /^https:\/\/app\.myaifrontdesk\.com/],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		beforeSend(event) {
			const message = event?.exception?.values?.[0]?.value || '';
			if (message.includes('Failed to update a ServiceWorker') && message.includes('/sw.js')) {
				return null;
			}
			return event;
		},
	});
}

const App = () => {
	useEffect(() => {
		const getSavedLanguage = (): string | null => {
			try {
				const match = document.cookie.match(/(^|;)\s*googtrans=([^;]+)/);
				if (match) {
					const langPair = decodeURIComponent(match[2]);
					const targetLang = langPair.split('/')[2];
					if (targetLang && targetLang !== 'en') {
						return targetLang;
					}
				}

				const htmlElement = document.querySelector('html');
				if (htmlElement && htmlElement.lang && htmlElement.lang !== 'en') {
					return htmlElement.lang;
				}
			} catch (error) {
				console.log('Error getting saved language:', error);
			}
			return null;
		};

		const isTranslationAlreadyActive = (): boolean => {
			const body = document.body;
			if (!body) {
				return false;
			}

			return !!(
				body.classList.contains('translated-ltr') ||
				body.classList.contains('translated-rtl') ||
				document.querySelector('.goog-te-combo') ||
				(document.documentElement && document.documentElement.lang !== 'en')
			);
		};

		const savedLanguage = getSavedLanguage();
		if (savedLanguage && !isTranslationAlreadyActive()) {
			// Add delay to avoid Google's anti-bot detection
			const applyDelay = Math.random() * 2000 + 3000;

			const timeoutId = setTimeout(() => {
				if (!isTranslationAlreadyActive()) {
					applyTranslation(savedLanguage).catch((error) => {
						console.log('Could not auto-apply saved language:', error);
					});
				}
			}, applyDelay);

			return () => clearTimeout(timeoutId);
		}
	}, []);

	return (
		<Provider store={store}>
			<QueryClientProvider client={queryClient}>
				<ConfigProvider
					theme={{
						token: {
							fontFamily: 'DM Sans, ui-sans-serif, system-ui, sans-serif',
						},
					}}
				>
					<GlobalProvider>
						<ResellerGlobalProvider>
							<AudioPlayerProvider>
								<PostHogProvider
									apiKey={import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
									options={options}
								>
									<VersionChecker />
									<AppRouter />
									<ToastContainer />
									{/* Hidden element for Google Translate */}
									<div id="google_translate_trigger" style={{ display: 'none' }} />
								</PostHogProvider>
								<AudioPlayerToolbar />
							</AudioPlayerProvider>
						</ResellerGlobalProvider>
					</GlobalProvider>
				</ConfigProvider>
			</QueryClientProvider>
		</Provider>
	);
};

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<ErrorBoundary>
			<App />
		</ErrorBoundary>
	</React.StrictMode>,
);
